"use client";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useTheme } from "next-themes";
import {
  createApolloClient,
  refreshToken,
} from "@/api/apollo-client/apollo-client";
import {
  ApolloLink,
  ApolloProvider,
  useMutation,
  useQuery,
} from "@apollo/client";
import { PrivacyLevelEnum } from "@/constants";
import {
  LOGIN_QUERY,
  QUERY_USER,
  GET_INFLUENCE_COUNT,
  SIGNUP_MUTATION,
  SEND_OTP_MUTATION,
  VERIFY_OTP_MUTATION,
  UPDATE_USER_MUTATION,
} from "@/api/apollo-client/authQueries";
import { useRouter } from "next/navigation";
import { LOGOUT } from "@/api/apollo-client/mutations/mutations";
import { CHECK_IS_ADMIN } from "@/api/apollo-client/queries/Queries";
import { Console } from "console";
import toast from "react-hot-toast";

interface Geo {
  type: string;
  coordinates: number[];
}

export enum Status {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  BANNED = "BANNED",
  DELETED = "DELETED",
}

export enum VerificationStatus {
  UNVERIFIED = "UNVERIFIED",
  EMAIL_VERIFIED = "EMAIL_VERIFIED",
  IDENTITY_VERIFIED = "IDENTITY_VERIFIED",
}

export enum AccountType {
  CITIZEN = "CITIZEN",
  ORGANIZATION = "ORGANIZATION",
  PUBLIC_FIGURE = "PUBLIC_FIGURE",
}

interface Post {
  id: string;
  title: string;
  content?: string;
  author?: User;
}

interface Media {
  id: string;
  type: string;
  url: string;
  thumbnail_url: string;
}

interface User {
  id: string;
  name: string;
  username: string;
  display_name: string;
  first_name?: string;
  last_name?: string;
  legal_name?: string;
  date_of_establishment?: string;
  email_address: string;
  profile_bio: string;
  privacy_level: PrivacyLevelEnum;
  problem_praise_tab: PrivacyLevelEnum;
  status: Status;
  verification_status: VerificationStatus;
  is_political_party?: boolean;
  party_election_symbol?: Media | null;
  account_type: AccountType;
  createdAt: string;
  updated_at: string;
  profile_picture?: Media | null;
  location?: Geo | null;
  follower_count?: number;
  following_count?: number;
  patron_count?: number;
  patronage_count?: number;
  influence_count?: number;
  isAdmin?: boolean;
}

interface AuthPayload {
  request_status: string;
  User?: User;
  id?: string;
  access_token?: string;
}

interface UserUpdateInput {
  user_id: string;
  name?: string;
  username?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  profile_bio?: string;
  legal_name?: string;
  date_of_establishment?: string;
  account_type?: AccountType;
  privacy_level?: string;
  email_address?: string;
  location?: {
    type: string;
    coordinates: number[];
  };
  profile_picture?: string; // ID
  profile_banner?: string; // ID
  party_election_symbol?: string; // ID
}
interface AssociationResponse {
  response: string;
}
interface AuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: User | null;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  signup: (email: string, password: string, name: string) => Promise<void>;
  handleGoogleAuth: (token: string, isSignup?: boolean) => Promise<void>;
  token: string | null;
  getUserById: (id: string) => Promise<User>;
  getUserByName: (name: string) => Promise<User>;
  getInfluenceCount: (
    userId: string,
    level?: number
  ) => Promise<{ influence_count: number; level?: number }>;
  sendOtp: (userId: string) => Promise<string>;
  verifyOtp: (userId: string, otpCode: number) => Promise<string>;
  updateUser: (input: UserUpdateInput) => Promise<User>;
  refreshUserData: () => Promise<void>;
  refreshToken: (httpLink: ApolloLink) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  login: async () => {},
  logout: () => {},
  signup: async () => {},
  handleGoogleAuth: async () => {},
  token: null,
  getUserById: async () => ({
    id: "",
    name: "",
    username: "",
    display_name: "",
    email_address: "",
    profile_bio: "",
    privacy_level: PrivacyLevelEnum.PUBLIC,
    problem_praise_tab: PrivacyLevelEnum.PUBLIC,
    status: Status.ACTIVE,
    verification_status: VerificationStatus.UNVERIFIED,
    account_type: AccountType.CITIZEN,
    createdAt: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile_picture: null,
    location: null,
    party_election_symbol: null,
  }),
  getUserByName: async () => ({
    id: "",
    name: "",
    username: "",
    display_name: "",
    email_address: "",
    profile_bio: "",
    privacy_level: PrivacyLevelEnum.PUBLIC,
    problem_praise_tab: PrivacyLevelEnum.PUBLIC,
    status: Status.ACTIVE,
    verification_status: VerificationStatus.UNVERIFIED,
    account_type: AccountType.CITIZEN,
    createdAt: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile_picture: null,
    location: null,
    party_election_symbol: null,
  }),
  getInfluenceCount: async () => ({ influence_count: 0 }),
  sendOtp: async () => "",
  verifyOtp: async () => "",
  updateUser: async () => ({
    id: "",
    name: "",
    username: "",
    display_name: "",
    email_address: "",
    profile_bio: "",
    privacy_level: PrivacyLevelEnum.PUBLIC,
    problem_praise_tab: PrivacyLevelEnum.PUBLIC,
    status: Status.ACTIVE,
    verification_status: VerificationStatus.UNVERIFIED,
    account_type: AccountType.CITIZEN,
    createdAt: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile_picture: null,
    location: null,
    party_election_symbol: null,
  }),
  refreshUserData: async () => {},
  refreshToken: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const { setTheme } = useTheme();
  const router = useRouter();
  // Do not mirror tokens into localStorage; rely on HttpOnly cookies.

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsInitialized(true);
      }
      // const justLoggedIn = localStorage.getItem("just_logged_in");
      // ({justLoggedIn });
      // if (justLoggedIn === "true") {
      //   localStorage.removeItem("just_logged_in");
      //   router.push("/"); // ✅ auto-redirect only once
      // }
      // Clear the flag
      localStorage.removeItem("just_logged_in");
    };

    initializeAuth();
  }, []);

  const apolloClient = useMemo(() => {
    return createApolloClient(token);
  }, [token]);

  // Query definitions

  // Login function
  const login = async (identifier: string, password: string) => {
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
    const loginVariables = {
      password,
      email_address: isEmail ? identifier : null,
      name: isEmail ? null : identifier,
    };
    try {
      const { data, errors } = await apolloClient.query({
        query: LOGIN_QUERY,
        variables: loginVariables,
        fetchPolicy: "network-only",
        errorPolicy: "all",
      });

      if (errors && errors.length > 0) {
        const error = errors[0];
        if (error.message.includes("401")) {
          throw new Error("Incorrect email or password");
        } else if (error.message.includes("404")) {
          throw new Error("Email address not found");
        } else {
          throw new Error(`Authentication failed: ${error.message}`);
        }
      }

      if (!data?.verifyLogin) {
        throw new Error("Authentication failed. Please try again.");
      }

      const { access_token, refresh_token } = data.verifyLogin;

      const { data: userData } = await apolloClient.query({
        query: QUERY_USER,
        variables: {
          input: {
            email_address: isEmail ? identifier : null,
            name: isEmail ? null : identifier,
          },
        },
        fetchPolicy: "network-only",
      });

      if (!userData?.queryUser) {
        throw new Error("User data not found");
      }
      // const userInfo = userData.queryUser;

      const { data: adminCheck } = await apolloClient.query({
        query: CHECK_IS_ADMIN,
        fetchPolicy: "network-only",
      });

      const isAdmin = adminCheck?.isAdmin ?? false;

      // Attach to user object
      const initialUserInfo = userData.queryUser;

      const userInfo = { ...initialUserInfo, isAdmin: isAdmin };

      // Rely on backend to set cookies during login flow; avoid client-side cookie setting.

      setUser(userInfo);
      setIsAuthenticated(true);
      setTheme("dark");

      // Persist only non-sensitive user info for UX; avoid token storage
      localStorage.setItem("user", JSON.stringify(userInfo));
    } catch (error) {
      console.log("LOGIN ERROR DETAILS:", error);

      if (error && typeof error === "object" && "graphQLErrors" in error) {
        const gqlError = (error as { graphQLErrors: any[] }).graphQLErrors[0];
        if (gqlError) {
          if (
            gqlError.extensions?.code === "UNAUTHENTICATED" ||
            gqlError.extensions?.code === "FORBIDDEN"
          ) {
            throw new Error("Invalid credentials");
          } else if (gqlError.extensions?.code === "NOT_FOUND") {
            throw new Error("Email address not found");
          }
        }
      }

      throw error instanceof Error
        ? error
        : new Error("An error occurred during login. Please try again.");
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      const { data, errors } = await apolloClient.mutate({
        mutation: SIGNUP_MUTATION,
        variables: { name, email_address: email, password },
        errorPolicy: "all",
      });

      if (errors && errors.length > 0) {
        const errorMessage = errors[0].message;
        if (errorMessage.includes("409")) {
          throw new Error("Username or email already exists");
        } else {
          throw new Error(`Signup failed: ${errorMessage}`);
        }
      }

      if (!data?.addUser) {
        throw new Error("Invalid response from server during signup");
      }

      if (data.addUser.request_status === "user added") {
        if (data.addUser.access_token && data.addUser.refresh_token) {
          const { access_token, refresh_token, id } = data.addUser;

          // Do not persist tokens; rely on cookies issued by backend

          try {
            const { data: userData } = await apolloClient.query({
              query: QUERY_USER,
              variables: { input: { email_address: email } },
            });

            if (userData?.queryUser) {
              const userInfo = userData.queryUser;
              setUser(userInfo);
              localStorage.setItem("user", JSON.stringify(userInfo));
              setIsAuthenticated(true);
              setTheme("dark");
            } else {
              const minimalUser: User = {
                id: id || "",
                name,
                username: name,
                display_name: name,
                email_address: email,
                profile_bio: "",
                privacy_level: PrivacyLevelEnum.PUBLIC,
                problem_praise_tab: PrivacyLevelEnum.PUBLIC,
                status: Status.ACTIVE,
                verification_status: VerificationStatus.UNVERIFIED,
                is_political_party: false,
                account_type: AccountType.CITIZEN,
                createdAt: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                profile_picture: null,
                location: null,
                follower_count: 0,
                following_count: 0,
                patron_count: 0,
                patronage_count: 0,
                influence_count: 0,
                party_election_symbol: null,
              };

              setUser(minimalUser);
              localStorage.setItem("user", JSON.stringify(minimalUser));
              setIsAuthenticated(true);
            }
          } catch (userError) {
            console.error(
              "AuthProvider: Error fetching user after signup:",
              userError
            );

            const minimalUser: User = {
              id: id || "",
              name,
              username: name,
              display_name: name,
              email_address: email,
              profile_bio: "",
              privacy_level: PrivacyLevelEnum.PUBLIC,
              problem_praise_tab: PrivacyLevelEnum.PUBLIC,
              status: Status.ACTIVE,
              verification_status: VerificationStatus.UNVERIFIED,
              is_political_party: false,
              account_type: AccountType.CITIZEN,
              createdAt: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              profile_picture: null,
              location: null,
              follower_count: 0,
              following_count: 0,
              patron_count: 0,
              patronage_count: 0,
              influence_count: 0,
              party_election_symbol: null,
            };

            setUser(minimalUser);
            localStorage.setItem("user", JSON.stringify(minimalUser));
            setIsAuthenticated(true);
            setTheme("dark");
          }
        } else {
          await login(email, password);
        }
      } else {
        throw new Error(data.addUser.request_status || "Signup failed");
      }
    } catch (error) {
      throw error;
    }
  };

  const sendOtp = async (userId: string): Promise<string> => {
    try {
      const { data, errors } = await apolloClient.mutate({
        mutation: SEND_OTP_MUTATION,
        variables: { user_id: userId },
        errorPolicy: "all",
      });

      if (errors && errors.length > 0) {
        throw new Error(`Failed to send OTP: ${errors[0].message}`);
      }

      if (data?.sendOtp) {
        return data.sendOtp;
      } else {
        throw new Error("Failed to send OTP");
      }
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (
    userId: string,
    otpCode: number
  ): Promise<string> => {
    try {
      const { data, errors } = await apolloClient.mutate({
        mutation: VERIFY_OTP_MUTATION,
        variables: { user_id: userId, otp_code: otpCode },
        errorPolicy: "all",
      });

      if (errors && errors.length > 0) {
        throw new Error(`Failed to verify OTP: ${errors[0].message}`);
      }

      if (data?.verifyOtp) {
        if (data.verifyOtp === "email has been verified" && user) {
          const updatedUser = {
            ...user,
            verification_status: VerificationStatus.EMAIL_VERIFIED,
          };
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }

        return data.verifyOtp;
      } else {
        throw new Error("Failed to verify OTP");
      }
    } catch (error) {
      throw error;
    }
  };

  const updateUser = async (input: UserUpdateInput): Promise<User> => {
    try {
      let mutationInput = { ...input };

      // Ensure profile_picture is properly set (ID only)
      if (input.profile_picture && typeof input.profile_picture !== "string") {
        // If profile_picture is an object, extract the ID
        mutationInput.profile_picture =
          (input.profile_picture as any).id || input.profile_picture;
      }

      if (
        input.party_election_symbol &&
        typeof input.party_election_symbol !== "string"
      ) {
        // If profile_picture is an object, extract the ID
        mutationInput.party_election_symbol =
          (input.party_election_symbol as any).id ||
          input.party_election_symbol;
      }

      // Provide stubs for new fields if not present
      mutationInput.account_type = mutationInput.account_type || undefined;
      mutationInput.privacy_level = mutationInput.privacy_level || undefined;
      mutationInput.first_name = mutationInput.first_name || undefined;
      mutationInput.last_name = mutationInput.last_name || undefined;

      // Perform the mutation
      const { data, errors } = await apolloClient.mutate({
        mutation: UPDATE_USER_MUTATION,
        variables: { input: mutationInput },
        errorPolicy: "all",
      });

      // Check for GraphQL errors and throw if necessary
      if (errors && errors.length > 0) {
        throw new Error(
          `Failed to update user: ${errors.map((err) => err.message).join(", ")}`
        );
      }

      // If mutation succeeded, fetch the latest user data using the current user ID
      const userId = input.user_id || user?.id;
      if (!userId) {
        throw new Error("No user ID available to refresh user data");
      }

      try {
        const { data: freshUserData } = await apolloClient.query({
          query: QUERY_USER,
          variables: { input: { user_id: userId } },
          fetchPolicy: "network-only",
        });
        if (freshUserData?.queryUser) {
          setUser(freshUserData.queryUser);

          localStorage.setItem("user", JSON.stringify(freshUserData.queryUser));
          return freshUserData.queryUser;
        }
        throw new Error("Failed to fetch updated user data");
      } catch (err) {
        throw err;
      }
    } catch (error) {
      throw error;
    }
  };

  const getUserById = async (user_id: string): Promise<User> => {
    try {
      const { data } = await apolloClient.query({
        query: QUERY_USER,
        variables: { input: { user_id } },
      });

      if (data?.queryUser) {
        const userData: User = {
          id: data.queryUser.id,
          name: data.queryUser.name,
          username: data.queryUser.username,
          display_name: data.queryUser.display_name || data.queryUser.name,
          first_name: data.queryUser.first_name,
          last_name: data.queryUser.last_name,
          email_address: data.queryUser.email_address,
          profile_bio: data.queryUser.profile_bio,
          privacy_level: data.queryUser.privacy_level,
          problem_praise_tab:
            data.queryUser.problem_praise_tab || PrivacyLevelEnum.PUBLIC,
          status: data.queryUser.status,
          account_type: data.queryUser.account_type,
          verification_status: data.queryUser.verification_status,
          is_political_party: data.queryUser.is_political_party,
          createdAt: data.queryUser.createdAt,
          updated_at: data.queryUser.updated_at,
          profile_picture: data.queryUser.profile_picture || null,
          location: data.queryUser.location || null,
          follower_count: data.queryUser.follower_count || 0,
          following_count: data.queryUser.following_count || 0,
          patron_count: data.queryUser.patron_count || 0,
          patronage_count: data.queryUser.patronage_count || 0,
          influence_count: data.queryUser.influence_count || 0,
          party_election_symbol: data.queryUser.party_election_symbol || null,
        };
        setUser(userData);
        return userData;
      } else {
        throw new Error("User not found");
      }
    } catch (error) {
      throw error;
    }
  };

  const getUserByName = async (name: string): Promise<User> => {
    try {
      const { data } = await apolloClient.query({
        query: QUERY_USER,
        variables: { input: { name } },
      });

      if (data?.queryUser) {
        const userData: User = {
          id: data.queryUser.id,
          name: data.queryUser.name,
          username: data.queryUser.username,
          display_name: data.queryUser.display_name || data.queryUser.name,
          first_name: data.queryUser.first_name,
          last_name: data.queryUser.last_name,
          email_address: data.queryUser.email_address,
          profile_bio: data.queryUser.profile_bio,
          privacy_level: data.queryUser.privacy_level,
          problem_praise_tab:
            data.queryUser.problem_praise_tab || PrivacyLevelEnum.PUBLIC,
          status: data.queryUser.status,
          account_type: data.queryUser.account_type,
          verification_status: data.queryUser.verification_status,
          is_political_party: data.queryUser.is_political_party,
          createdAt: data.queryUser.createdAt,
          updated_at: data.queryUser.updated_at,
          profile_picture: data.queryUser.profile_picture || null,
          location: data.queryUser.location || null,
          follower_count: data.queryUser.follower_count || 0,
          following_count: data.queryUser.following_count || 0,
          patron_count: data.queryUser.patron_count || 0,
          patronage_count: data.queryUser.patronage_count || 0,
          influence_count: data.queryUser.influence_count || 0,
          party_election_symbol: data.queryUser.party_election_symbol || null,
        };
        setUser(userData);
        return userData;
      } else {
        throw new Error("User not found");
      }
    } catch (error) {
      throw error;
    }
  };

  const getInfluenceCount = async (userId: string, level?: number) => {
    try {
      const { data } = await apolloClient.query({
        query: GET_INFLUENCE_COUNT,
        variables: { user_id: userId, level },
      });

      if (data?.getInfluenceCount) {
        return data.getInfluenceCount;
      } else {
        return { influence_count: 0, level };
      }
    } catch (error) {
      throw error;
    }
  };

  const refreshUserData = async () => {
    if (!user?.id || !token) return null;

    try {
      const { data, errors } = await apolloClient.query({
        query: QUERY_USER,
        variables: { input: { user_id: user.id } },
        fetchPolicy: "network-only", // Force a network request
        errorPolicy: "all",
      });

      if (errors && errors.length > 0) {
        console.error("AuthProvider: Error refreshing user data:", errors);
        return null;
      }

      if (data.queryUser) {
        setUser(data.queryUser);
        localStorage.setItem("user", JSON.stringify(data.queryUser));
        return data.queryUser;
      }

      return null;
    } catch (error) {
      console.error("AuthProvider: Failed to refresh user data:", error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await apolloClient.mutate({
        mutation: LOGOUT,
        errorPolicy: "all",
      });
    } catch (error) {
      console.error("Error during GraphQL logout:", error);
    }

    // Backend should clear cookies on its own logout endpoint; no client cookie handling

    // Clear localStorage and session state
    localStorage.removeItem("user");

    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    apolloClient.resetStore();
  };

  const handleGoogleAuth = async (
    idToken: string,
    isSignup: boolean = false
  ) => {
    try {
      const endpoint = `${process.env.NEXT_PUBLIC_BACKEND_URI}/auth/${isSignup ? "google-signup" : "google-login"}`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id_token: idToken }),
      });

      const result = await res.json();

      // Do not persist tokens; cookies are set by backend

      // Fetch user data
      const { data: userData } = await apolloClient.query({
        query: QUERY_USER,
        variables: { input: { user_id: result.user_id } },
      });

      if (userData?.queryUser) {
        const userInfo = userData.queryUser;

        setUser(userInfo);
        localStorage.setItem("user", JSON.stringify(userInfo));
        setIsAuthenticated(true);
        setTheme("dark");
      }

      return userData?.queryUser;
    } catch (error) {
      console.error("Google auth error:", error);
      throw error;
    }
  };

  const contextValue = {
    isAuthenticated,
    isInitialized,
    user,
    login,
    logout,
    signup,
    handleGoogleAuth,
    token,
    getUserById,
    getUserByName,
    getInfluenceCount,
    sendOtp,
    verifyOtp,
    updateUser,
    refreshUserData,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </AuthContext.Provider>
  );
};
